/*
 * Every sentence the Firewall screen shows, in one place, so the screen, its
 * confirmations and its tests cannot disagree. The words describe what the
 * appliance reports (cloud-anchor/pkg/firewall); they never decide anything.
 */

import { fmtBytesDecimal, fmtDuration } from "../utils/formatters";

/* A single address is shown as one, not as a network of one. */
export const shown = (net) => String(net || "").replace(/\/(32|128)$/, "");

export const count = (n) => Number(n || 0).toLocaleString();

export const plural = (n, one, many = `${one}s`) => `${count(n)} ${Number(n) === 1 ? one : many}`;

const clock = (iso) => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hourCycle: "h23" });
};

const day = (iso) => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleDateString([], { day: "numeric", month: "short" });
};

const listWords = (items) => (items.length < 2 ? items.join("") : `${items.slice(0, -1).join(", ")} and ${items[items.length - 1]}`);

/* The screen's sections, in the order its menu shows them, each with what it
   is for in a sentence. Across sites needs the portal, which compares the
   sites. */
export const SECTIONS = [
  { key: "overview", label: "Overview", about: "What the firewall is doing right now, and anything unusual the AI analyst noticed on your devices." },
  { key: "rules", label: "Rules", about: "What your devices may start (outbound) and who may reach this box (inbound), laid out like a cloud security group, plus blocking of known-bad addresses and whole countries." },
  { key: "intrusions", label: "Intrusions", about: "Bans addresses that keep failing to sign in or scan the box, for longer the riskier they look, and blocks attackers your other sites and the Weavian fleet banned." },
  { key: "groups", label: "Device groups", about: "Keep devices such as tills or cameras to the few places they need. The box can learn those places for you." },
  { key: "across", label: "Across sites", portal: true, about: "Compares each device group with the same group at your other sites, and points out a site whose devices go somewhere the others do not." },
  { key: "activity", label: "Activity", about: "What the firewall stopped: counts by kind, each blocked attempt with the rule that stopped it, and sending them to your own log system." },
];

/* The state at the top of every section, and how it feels. blocksSome says
   a group blocks some kind of traffic, so the promise says so too. */
export function status(v, blocksSome) {
  if (v.on && !v.in_force) {
    return { tone: "bad", title: "Needs attention", text: `The firewall is on, but the box could not put its rules in place: ${v.problem}` };
  }
  if (v.on) {
    return {
      tone: "on",
      title: "Protected",
      text: `Strangers on the internet cannot reach this box or your network. Everything your devices start works as usual: websites, Netflix, Zoom calls, card payments${blocksSome ? ", unless you block it for a group" : ""}.`,
    };
  }
  return {
    tone: "off",
    title: "Not protected",
    text: "The firewall is off. Anyone who can reach this box's internet lines can try its settings and command line, and nothing coming in is filtered.",
  };
}

/* The one window every count on this screen covers: since the rules went in,
   when the firewall was turned on or the box last started. */
const since = (v) => (v.since ? `since the rules went in at ${clock(v.since)}` : "");

/* What came in uninvited and was stopped: from the internet side and over
   the Weavian cloud. The drawing and the protection list show this one number. */
const uninvited = (v) => Number(v.dropped?.internet || 0) + Number(v.dropped?.tunnel || 0);

export function turnOnAsk(v) {
  const n = (v.management_networks || []).length;
  const also = n ? `, and the ${n === 1 ? "network" : `${n} networks`} you listed` : "";
  return {
    title: "Turn the firewall on?",
    message: `From the internet, only replies to what your devices started will get through. The box's settings and command line will answer only its own network, the Weavian cloud${also}. If this console loses the box, the box turns the firewall back off by itself within two minutes.`,
    confirmLabel: "Turn it on",
  };
}

export const TURN_OFF_ASK = {
  title: "Turn the firewall off?",
  message: "Nothing will be filtered: anyone who can reach this box's internet lines can try to sign in to its command line and settings.",
  confirmLabel: "Turn it off",
  variant: "danger",
};

/* A change in force and waiting for this console to confirm it. */
export const WAITING =
  "Checking that this console still reaches the box after your change. If it cannot, the box puts back the settings from before.";

/* The switch's own words while a change waits: the time left. */
export function checkingWords(v, now) {
  const left = Math.max(0, Math.round((new Date(v.change?.until).getTime() - now) / 1000));
  return `Checking ${Math.floor(left / 60)}:${String(left % 60).padStart(2, "0")}`;
}

export const SWITCH_LABEL = "Firewall";

/* What the two-minute undo guards, said no wider than it is: it knows only
   whether this console still reaches the box. */
export const SAFETY =
  "If a change cuts this console off from the box, the box puts back the settings from before within two minutes.";

/* The last change the box undid on its own. */
export const undoneWords = (v) =>
  `Your change was undone at ${clock(v.undone?.at)}: this console could not confirm that it still reached the box afterwards, so the box went back to the settings before it.`;

export const UNREADABLE =
  "The box could not read its firewall settings, so it kept itself closed with no management networks. Saving any change here replaces them.";

/* Overview: the drawing of what crosses the box. */
export const FLOW = { lan: "Your network", lanSub: "computers, tills, phones", box: "This box", internet: "The internet", internetSub: "everyone else" };

export function flowWords(v) {
  const n = uninvited(v);
  return {
    boxSub: v.on ? "firewall on" : "firewall off",
    out: "Started by your devices",
    outVerdict: "allowed, replies come back",
    in: "Started from outside",
    inVerdict: v.on ? (n ? `blocked · ${count(n)}` : "blocked") : "let through",
    aria: v.on
      ? "What your devices start goes out and its replies come back; what is started from outside stops at this box."
      : "What your devices start goes out; with the firewall off, what is started from outside reaches the box and your network.",
  };
}

/* Overview: what the firewall does, one card each, with what it is doing now. */
export const FEATURES = {
  strangers: {
    title: "Keeps strangers out",
    means: "Nobody on the internet can open a connection to your devices or to this box. Replies to what your devices start always come back.",
    action: "See what it blocked",
  },
  manage: {
    title: "Only you can manage it",
    means: "The box's settings and command line answer only its own network, the Weavian cloud and the places you list.",
    action: "Manage access",
  },
  apart: {
    title: "Keeps your locations apart",
    means: "Devices here cannot reach your other locations' boxes. Devices you share on Remote Devices still connect.",
  },
  groups: {
    title: "Holds devices to what they need",
    means: "Put tills, terminals or cameras in a group and they reach only the places you allow. Everything else they try is blocked.",
  },
  learn: {
    title: "Learns what is normal",
    means: "Watches a group's devices for a few days and suggests exactly which places to allow, so nobody has to guess addresses.",
  },
  compare: {
    title: "Compares your locations",
    means: "Checks that the same group behaves the same at every location, and flags a site whose devices go somewhere the others do not.",
    action: "Compare sites",
  },
  lists: {
    title: "Blocks known-bad addresses",
    means: "Public lists of criminal networks, botnet controllers and broken-into machines, fetched every six hours.",
  },
  countries: {
    title: "Blocks countries",
    means: "Keeps your network from reaching whole countries, and them from reaching the box.",
  },
  flood: {
    title: "Limits floods",
    means: "One address opening connections or pinging faster than anyone managing the box would is slowed to a stop.",
  },
  bans: {
    title: "Bans attackers",
    means: "An address that keeps failing to sign in to the box, or tries port after port, is banned: minutes for a likely typo, a month for an attack. Works with the firewall on or off.",
    action: "See bans",
  },
  shared: {
    title: "Blocks attackers seen elsewhere",
    means: "What your other sites and the Weavian fleet banned, what Weavian's AI found guessing slowly across many sites, and crowdsourced attacker lists.",
  },
  rules: {
    title: "Your own rules",
    means: "Block or allow by device, place, port and time of day, such as no mail sent from the tills, or the tills off the internet while the shop is closed.",
  },
};

export const TURN_ON = "Turn on protection";

export const PROTECTION = {
  title: "Protection",
  on: "Working now, on every line into this box.",
  off: "Off. Turn the firewall on and all of this starts at once.",
};

export const SMART = {
  title: "Smart controls",
  sub: "For devices that should only talk to a few places, like tills, terminals and cameras.",
};

/* What one protection is doing on this box now: a state in a word or two,
   and a detail under it. */
export function protectNow(key, v) {
  const on = !!v.on;
  const off = { tone: "off", state: "Off", detail: "" };
  switch (key) {
    case "strangers":
      return on ? { tone: "on", state: `${count(uninvited(v))} blocked`, detail: since(v) } : { ...off, detail: "nothing coming in is filtered" };
    case "manage": {
      const n = (v.management_networks || []).length;
      return on ? { tone: "on", state: "Restricted", detail: n ? `and ${plural(n, "network")} you listed` : "this network and the Weavian cloud" } : { ...off, detail: "anyone can try" };
    }
    case "lists": {
      const n = (v.lists || []).reduce((sum, l) => sum + Number(l.entries || 0), 0);
      if (!v.blocklists) return { tone: "off", state: "Off", detail: "" };
      if (!on) return { ...off, detail: "set, waiting for the firewall" };
      return n ? { tone: "on", state: `${count(v.dropped?.threat || 0)} stopped`, detail: `${plural(n, "address range")} listed` }
        : { tone: "warn", state: "Fetching", detail: "the lists have not arrived yet" };
    }
    case "countries": {
      const n = (v.block_countries || []).length;
      if (!n) return { tone: "off", state: "None", detail: "" };
      if (!on) return { ...off, detail: `${plural(n, "country", "countries")} set, waiting for the firewall` };
      if (v.countries_pending) return { tone: "warn", state: "Loading", detail: `${plural(n, "country", "countries")}` };
      return { tone: "on", state: `${count(v.dropped?.country || 0)} stopped`, detail: listWords(v.block_countries.slice(0, 3).map(countryName)) + (n > 3 ? ` and ${n - 3} more` : "") };
    }
    case "flood":
      return on ? { tone: "on", state: "On", detail: `${plural(v.dropped?.flood || 0, "try", "tries")} slowed` } : off;
    case "bans": {
      const j = v.jails;
      if (!j) return { tone: "off", state: "Not on this build", detail: "" };
      const n = (j.bans || []).length;
      const on = (j.jails || []).some((x) => x.enabled);
      if (!on) return { tone: "off", state: "Off", detail: "every jail switched off" };
      return { tone: "on", state: n ? `${plural(n, "address", "addresses")} banned` : "Watching", detail: `${count(j.dropped?.jail || 0)} tries stopped` };
    }
    case "shared": {
      const j = v.jails;
      if (!j) return { tone: "off", state: "Not on this build", detail: "" };
      const n = Number(j.fleet?.fleet?.entries || 0) + Number(j.fleet?.feeds?.entries || 0);
      if (!n) return { tone: "off", state: "Nothing shared yet", detail: "" };
      return { tone: "on", state: plural(n, "address", "addresses"), detail: `${count(Number(j.dropped?.fleet || 0) + Number(j.dropped?.feeds || 0))} tries stopped` };
    }
    case "rules": {
      const n = (v.rules || []).length;
      if (!n) return { tone: "off", state: "None yet", detail: "" };
      return on ? { tone: "on", state: plural(n, "rule"), detail: "" } : { ...off, detail: `${plural(n, "rule")} set, waiting for the firewall` };
    }
    default:
      return on ? { tone: "on", state: "On", detail: `${plural(v.dropped?.lan_to_boxes || 0, "attempt")} stopped` } : { ...off, detail: "other boxes reachable" };
  }
}

/* A group's state in a word or three: locked to its places, learning them, or open. */
export function groupState(profile, watched) {
  const n = (profile.destinations || []).length;
  if (n) return { tone: "on", label: `Locked to ${plural(n, "place")}` };
  if (watched) return { tone: "ai", label: "Learning" };
  return { tone: "off", label: "Open" };
}

export function groupsLive(profiles, watchedOf) {
  if (!profiles.length) return "No groups yet.";
  return profiles.slice(0, 3).map((p) => `${p.name}: ${groupState(p, watchedOf(p.id)).label.toLowerCase()}`).join(" · ") +
    (profiles.length > 3 ? ` · ${profiles.length - 3} more` : "");
}

export function learnLive(learned, nameOf) {
  const w = learned?.groups || [];
  if (!learned) return "Needs a box build that learns.";
  if (!w.length) return "Not watching any group yet.";
  const g = w[0];
  const more = w.length > 1 ? `, and ${plural(w.length - 1, "other group")}` : "";
  return `Watching ${nameOf(g.group)} since ${day(g.since)}: ${plural((g.places || []).length, "place")} so far${more}.`;
}

export function compareLive(fleet) {
  if (fleet.odd.length) return findingWords(fleet.odd[0].group, fleet.odd[0]);
  if (!fleet.groups.length) return "Not set up: watch a group with the same name at each site.";
  const g = fleet.groups[0];
  return `${g.name} is watched at ${plural(g.sites.length, "site")}.`;
}

export const DOES_NOT =
  "The firewall decides who may connect: by place, port, country, time of day and device group, and whole kinds of traffic, like video streaming, for a group. It does not block single websites, scan files for viruses or read inside encrypted traffic.";

/* Activity: what the rules stopped, a row each, with what it means.
   groupName names a device group by its id. */
export function activityRows(v, groupName = (id) => id) {
  const d = v.dropped || {};
  const rows = [];
  if (v.on) {
    rows.push({
      key: "internet", n: Number(d.internet || 0), tone: "calm",
      what: "From the internet and the networks this box is plugged into",
      why: "Mostly automated scans, and device discovery chatter from the networks this box is plugged into. Normal: nothing to do.",
    });
    if (v.tunnel) {
      rows.push({
        key: "tunnel", n: Number(d.tunnel || 0), tone: "calm",
        what: "Over the Weavian cloud, from anything but the cloud itself",
        why: "Other locations or devices trying to reach this box or your network directly, and late packets of connections already closed.",
      });
      rows.push({
        key: "boxes", n: Number(d.lan_to_boxes || 0), tone: "look",
        what: "From your network toward other locations' boxes",
        why: "A device here tried to reach another location's box. Devices shared on Remote Devices are not stopped.",
      });
    }
  }
  if (v.on && v.blocklists) {
    rows.push({
      key: "threat", n: Number(d.threat || 0), tone: "look",
      what: "To or from known-bad addresses",
      why: "From outside it is noise. From one of your devices it may mean the device is infected: the AI analyst names it.",
    });
  }
  if (v.on && (v.block_countries || []).length) {
    rows.push({
      key: "country", n: Number(d.country || 0), tone: "calm",
      what: "To or from the countries you blocked",
      why: "A device here or a service you use may live there; if something stopped working, look here first.",
    });
  }
  if (v.on) {
    rows.push({
      key: "flood", n: Number(d.flood || 0), tone: "calm",
      what: "Over the flood limits",
      why: "An address opening connections to the box, or pinging it, faster than anyone managing it would.",
    });
  }
  if (v.jails) {
    const d2 = v.jails.dropped || {};
    rows.push({
      key: "jail", n: Number(d2.jail || 0), tone: "calm",
      what: "From addresses this box banned",
      why: "Addresses that kept failing to sign in, or scanned the box. The Intrusions section lists them, and lets one back in.",
    });
    rows.push({
      key: "shared", n: Number(d2.fleet || 0) + Number(d2.feeds || 0), tone: "calm",
      what: "From attackers banned elsewhere",
      why: "Banned at your other sites or across the Weavian fleet, found by Weavian's AI, or on crowdsourced attacker lists.",
    });
  }
  for (const r of v.on ? v.rules || [] : []) {
    if (r.action !== "block") continue;
    rows.push({
      key: `r-${r.id}`, n: Number(d.rules?.[r.id] || 0), tone: "calm",
      what: `Your rule "${r.name}"`,
      why: ruleSentence(r, groupName(r.group)),
    });
  }
  for (const g of v.groups || []) {
    rows.push({
      key: `g-${g.id}`, n: Number(g.stopped || 0), tone: "look",
      what: `${g.name} trying somewhere not on its list`,
      why: "If this keeps growing, a device may need a place added, or it may be doing something it should not.",
    });
  }
  return rows;
}

/* Activity's first sentence: what came in uninvited and was turned away, and
   what this site's own devices were stopped from doing, in one window. */
export function activityLead(v) {
  const ruled = (v.on ? v.rules || [] : []).filter((r) => r.action === "block" && r.direction === "out");
  const own = Number(v.dropped?.lan_to_boxes || 0) + (v.groups || []).reduce((n, g) => n + Number(g.stopped || 0), 0) +
    ruled.reduce((n, r) => n + Number(v.dropped?.rules?.[r.id] || 0), 0);
  const parts = [];
  if (v.on) parts.push(`${plural(uninvited(v), "uninvited try", "uninvited tries")} turned away ${since(v)}.`);
  if (own) parts.push(`Your own devices were stopped ${plural(own, "time")}${v.on ? "" : ` ${since(v)}`}.`);
  return parts.join(" ") || NOTHING_STOPPED;
}

const NOTHING_STOPPED =
  "Nothing is being stopped: the firewall is off and no device group is locked to its places.";

/* Device groups: the profiles on Device Speed Limits, held to the places
   their devices need (cloud-anchor/pkg/devicectl destinations, enforced by
   pkg/firewall) and watched to learn those places (pkg/firewall learn.go). */
export const GROUPS = {
  title: "Device groups",
  means: "Keep devices that should talk to only a few places, like tills, betting terminals or cameras, to exactly those places.",
  shared: "A group is also a profile on Device Speed Limits, so it can carry a speed limit too.",
  create: "New group",
  emptyTitle: "Lock your fixed devices to what they need",
  steps: [
    { title: "Pick the devices", text: "Tills, terminals, cameras: anything that should only talk to a few servers." },
    { title: "Let the box learn", text: "For a few days it watches where they go. Nothing is blocked meanwhile." },
    { title: "Lock them", text: "One click allows what they used. Anything else they try is blocked and counted." },
  ],
  templates: ["Tills", "Betting terminals", "Cameras", "Card readers"],
};

export function membersWords(profile, view) {
  const n = Object.values(view?.devices || {}).filter((d) => d.profile === profile.id).length;
  const devices = plural(n, "device");
  return view?.new_devices === profile.id ? `${devices}, and every new device` : devices;
}

/* Whether an IPv4 address lies inside a network, "203.0.113.0/24". */
const V4 = /^\d+\.\d+\.\d+\.\d+$/;
const ipv4 = (a) => String(a).split(".").reduce((n, o) => n * 256 + Number(o), 0);
function within(address, net) {
  const [addr, bits = "32"] = String(net).split("/");
  if (!V4.test(addr) || !V4.test(address)) return false;
  const size = 2 ** (32 - Number(bits));
  const lo = Math.floor(ipv4(addr) / size) * size;
  return ipv4(address) >= lo && ipv4(address) < lo + size;
}

/* A network's owner as people say it. The table names each by its handle,
   sometimes followed by the organisation: "GOOGLE-AS-AP Google Asia Pacific
   Pte. Ltd." reads as the organisation, "LINODE-AP" as "Linode". */
const titled = (w) => (w === w.toUpperCase() ? w.charAt(0) + w.slice(1).toLowerCase() : w);
export function orgName(raw) {
  const name = String(raw || "").trim();
  if (!name) return "";
  const space = name.indexOf(" ");
  if (space > 0) return name.slice(space + 1).split(/\s+/).map(titled).join(" ");
  return titled(name.split("-")[0]);
}

/* Who runs a place: the owner the box knows for the places its devices used
   inside it, when they agree. */
export function ownerIn(net, places) {
  const owners = new Set((places || []).filter((p) => p.owner && within(p.address, net)).map((p) => p.owner));
  return owners.size === 1 ? [...owners][0] : "";
}

export function suggestionWords(w, n) {
  const days = Math.max(1, Math.round((Date.now() - new Date(w?.since).getTime()) / 86400000));
  return `From ${fmtDuration(days * 86400)} of watching, the box suggests ${plural(n, "place")}.`;
}
export const elsewhereWords = (n) => `used at ${plural(n, "other site")}`;
export const LEFT_OUT = "Left out";
export function leftOutWords(r, group) {
  const who = r.who.length ? listWords(r.who) : "One device";
  const at = r.ports ? ` on port ${r.ports}` : "";
  return `Only ${who} went to ${orgName(r.owner) || shown(r.net)}${at}, which ${group} at none of your other sites do. Find out why before you allow it.`;
}

export const lockTo = (name, n) => `Lock ${name} to ${n === 1 ? "this place" : `these ${count(n)} places`}`;
export const lockedWords = (name) => `${name} is locked: anything else its devices try is blocked from now on.`;
export const UNDO = "Undo";
export const UNDO_FOR_MS = 120000;

/* The line between a group's devices and where they may go. */
export function laneLabel(tone, watched) {
  if (tone === "on") return "only to";
  if (tone === "ai") return `learning, day ${Math.max(1, Math.ceil((Date.now() - new Date(watched?.since).getTime()) / 86400000))}`;
  return "anywhere";
}
export const stoppedStub = (n) => `${plural(n, "other try", "other tries")} stopped`;
export const moreDevices = (n) => `and ${count(n)} more`;
export const NO_DEVICES_YET = "No devices yet";
export const blockedKinds = (labels) => `Blocked for these devices: ${listWords(labels)}`;

/* When a place was last used, in days, then weeks and months. */
const dayWords = (iso) => {
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return "";
  const days = Math.floor((Date.now() - t) / 86400000);
  return days <= 0 ? "today" : days === 1 ? "yesterday" : `${fmtDuration(days * 86400, 1)} ago`;
};
export const SUGGEST_COLS = { who: "Who runs it", port: "Port", devices: "Devices", last: "Last used" };

/* What learning offers to lock a group to: each suggested place with who runs
   it, its ports, which of the group's devices used it and when last; the
   places the same group uses at the customer's other sites and not here yet,
   taken too; and the places only this site's group reaches, left out with
   the devices that went there named. */
export function suggestion(watched, { odd = [], fleetGroup = null, here = "", nameOfMac = (m) => m } = {}) {
  if (!watched) return { take: [], elsewhere: [], left: [] };
  const rows = (watched.suggested || []).map((net) => {
    const inside = (watched.places || []).filter((p) => p.answered && within(p.address, net));
    const ports = [...new Set(inside.map((p) => (p.port ? `${p.port}` : p.protocol)))];
    const last = inside.reduce((a, p) => (String(p.last || "") > a ? String(p.last) : a), "");
    const who = [...new Set(inside.flatMap((p) => p.who || []))].map(nameOfMac);
    const devices = who.length || Math.max(0, ...inside.map((p) => Number(p.devices || 0)));
    return {
      net, owner: ownerIn(net, watched.places), ports: ports.join(", "), who,
      devices: `${count(devices)} of ${count(watched.devices)}`, last: dayWords(last), odd: oddIn(net, odd),
    };
  });
  const near = (a, b) => within(String(a).split("/")[0], b) || within(String(b).split("/")[0], a);
  const elsewhere = (fleetGroup?.places || [])
    .filter((p) => !p.sites.includes(here) && p.sites.length > 0 && !rows.some((r) => near(r.net, p.net)))
    .map((p) => ({ net: p.net, owner: p.owner || "", ports: p.port ? `${p.port}` : p.proto, sites: p.sites.length }));
  return { take: rows.filter((r) => !r.odd), elsewhere, left: rows.filter((r) => r.odd) };
}

/* Whether a suggested place is one the server says only this site's group
   reaches: its /24 holds the odd place's network. */
function oddIn(net, odd) {
  const [addr] = String(net).split("/");
  const o24 = addr.split(".").slice(0, 3).join(".");
  return (odd || []).some((o) => String(o.place).split(" ")[0].split("/")[0].split(".").slice(0, 3).join(".") === o24);
}

/* Devices whose names say what they are, two or more of a kind in no group
   and with no group of that name: offered as one. */
const LOOKS = [
  { name: "Tills", re: /till|pos\b|kasa|register/i, what: "tills" },
  { name: "Betting terminals", re: /terminal|betting|kiosk/i, what: "betting terminals" },
  { name: "Cameras", re: /cam|nvr|dvr|hikvision|dahua/i, what: "cameras" },
  { name: "Printers", re: /print|epson|zebra/i, what: "printers" },
];
export function lookAlikes(rows, profiles) {
  const taken = new Set((profiles || []).map((p) => p.name.toLowerCase()));
  return LOOKS.map((l) => {
    const found = rows.filter((d) => !d.profile && l.re.test(d.name));
    return { ...l, names: found.map((d) => d.name), macs: found.map((d) => d.mac) };
  }).filter((l) => l.macs.length >= 2 && !taken.has(l.name.toLowerCase()));
}
export const alikeWords = (a) => `The box sees ${count(a.macs.length)} devices that look like ${a.what}: ${listWords(a.names.slice(0, 4))}${a.names.length > 4 ? " and more" : ""}.`;
export const makeGroup = (name) => `Make them ${name}`;
export const MAC = /^([0-9a-f]{2}:){5}[0-9a-f]{2}$/;
export const MAC_ADD = "One that is off? Add its hardware address, e.g. a8:29:48:d9:8c:a1";
export const NOT_SEEN = "not seen lately";
export const SAME_NAME = "Use the same name at every site, so the sites can be compared.";
export const OPTIONAL = "optional";
export const HOW_IT_WORKS = "How this works";
export const PICK_SHOWN = (n) => `Select ${n === 1 ? "it" : `all ${count(n)}`} not in other groups`;

/* A watched group's places by who runs them, most used first: one entry per
   owner, its address, or how many addresses when there are several. */
export function byOwner(places) {
  const groups = new Map();
  for (const p of places) {
    const key = p.owner || p.address;
    const g = groups.get(key) || { key, owner: p.owner || "", addresses: new Set(), devices: 0 };
    g.addresses.add(p.address);
    g.devices = Math.max(g.devices, Number(p.devices || 0));
    groups.set(key, g);
  }
  return [...groups.values()]
    .sort((a, b) => b.devices - a.devices || b.addresses.size - a.addresses.size)
    .map((g) => ({ ...g, where: g.addresses.size > 1 ? `${count(g.addresses.size)} addresses` : [...g.addresses][0] }));
}
export const morePlaces = (n) => `and ${count(n)} more`;
export const NOTHING_SEEN_YET = "Nothing seen yet";
export const UNPICK_SHOWN = "Clear these";

export function watchAsk(profile) {
  return {
    title: `Learn where devices in ${profile.name} connect?`,
    message: "The box records the places these devices connect to, and keeps the list on the box. Watch fixed equipment such as tills, terminals and cameras: what a phone or a laptop reaches belongs to the person carrying it.",
    confirmLabel: "Start learning",
  };
}

export const STOP_WATCH_ASK = {
  title: "Stop learning?",
  message: "The box forgets the places it recorded for this group.",
  confirmLabel: "Stop and forget",
  variant: "danger",
};

export const deleteAsk = (profile) => ({
  title: `Delete ${profile.name}?`,
  message: "Its devices go back to the router-wide settings: they can connect anywhere again, and lose any speed limit the group gave them.",
  confirmLabel: "Delete group",
  variant: "danger",
});

/* One place a watched group reached, as a row. */
export function placeWords(p) {
  const where = p.port === 53 ? `${p.address}, name lookups` : p.port ? `${p.address} port ${p.port}` : p.address;
  return { where: `${where} (${p.protocol})`, owner: p.owner || "", devices: p.devices, tried: !p.answered };
}

export const HOW = {
  learn: { title: "Learn first", tag: "Recommended", text: "The box watches where these devices go and suggests the list. Nothing is blocked until you lock it." },
  only: { title: "Only these places", text: "You know the servers. Anything else these devices try is blocked from the start." },
  anywhere: { title: "Anywhere", text: "No limit on where they go. You can still block kinds of traffic below." },
};

export const ANYWHERE = "Anywhere";
export const LOOKUPS_PASS = "Name lookups always get through, so the devices can still find their servers.";
export const HELD_LOSES =
  "A device in this group loses everything not listed, this console included if you open it from that device.";
export const NEW_DEVICES_HELD =
  "New devices join this group: every device that joins your network without a profile, the computer you are using included if it has none, will reach only the places listed.";
export const NEW_DEVICES_ASK = {
  title: "Hold every new device to these places?",
  message: "New devices join this group, so every device that joins your network without a profile will reach only the places listed. If the computer you are using has no profile, it loses this console unless the console's address is on the list.",
  confirmLabel: "Hold them",
  variant: "danger",
};
export const BLOCK_CAVEAT =
  "The first packets of a connection get through before the box recognises what it is, and traffic it does not recognise is not blocked.";
export const BLOCK_NEEDS_TUNNEL = "Blocking kinds of traffic needs this box to run through the Weavian cloud.";
export const USE_LEARNED = "Use what they reached";
export const TRIED = "Tried, never answered";
export const MOVES_FROM = (name) => `in ${name}`;
export const MOVES_NOTE = "A device can be in one group at a time: ticking one that is in another group moves it here.";
export const NO_DEVICES_SEEN = "The box has not seen any device on its network yet.";
export const PLACE_ADD = "An address or network, e.g. 203.0.113.9";

/* Across sites: this box's watched groups beside the same groups at the
   customer's other sites (server pkg/fleetwatch). A place reads as the server
   names it, "45.33.32.0/24 4444/tcp", with the network that owns it where its
   box knows that. */
export function fleetPlaceWords(place, owner) {
  const [where, port = ""] = String(place).split(" ");
  const at = owner ? `in ${where}, run by ${owner}` : `in ${where}`;
  const [num, proto] = port.split("/");
  return proto ? `port ${num} (${proto}) ${at}` : `${port} ${at}`;
}

/* A place as the server names it, "45.33.32.0/24 4444/tcp", so a row can be
   matched with the odd places the server lists. */
export const placeName = (p) => (p.port ? `${p.net} ${p.port}/${p.proto}` : `${p.net} ${p.proto}`);

const SERVICES = { 443: "web, secure", 80: "web", 53: "name lookups", 123: "time", 1883: "messaging", 8883: "messaging, secure", 554: "video stream", 5060: "calls", 5061: "calls, secure" };
export const portWords = (p) => {
  const what = SERVICES[p.port];
  const port = p.port ? `port ${p.port}` : p.proto;
  return `${p.net} · ${what ? `${port}, ${what}` : port}`;
};

export const matrixCaption = (group) => `Where ${group} connect at each site`;
export const siteColumn = "Site";

export const ACROSS = {
  title: "Same group, every location",
  placeColumn: "Where its devices go",
  here: "This site",
  means: "Each site that watches a group with the same name tells the Weavian cloud where the group's devices connect. A site whose group goes somewhere the others do not stands out, and its customer list gets an alert.",
  none: "No group watched here is watched at another of your sites yet.",
  steps: [
    "Make a group with the same name at each site, such as Tills.",
    "Turn on learning for it at each site.",
    "Once four sites report it, any place only one site's group reaches shows here.",
  ],
};

/* The finding, said first: this site's group goes somewhere no other site's
   does. */
export function findingWords(group, o) {
  const [where, port = ""] = String(o.place).split(" ");
  const [num] = port.split("/");
  const at = `${orgName(o.owner) || where}${num && port.includes("/") ? ` on port ${num}` : ""}`;
  return `${group} here connect to ${at}, which ${group} at none of your other ${plural(o.peers, "site")} do.`;
}
export const DETAILS = "Details";
export const findingQuestion = (group, o) =>
  `${group} at this site reach ${o.place}${o.owner ? ` (${o.owner})` : ""}, which no other site's ${group} use. What could that be, and what should I check?`;
export const reviewGroup = (group) => `Review ${group}`;

export function acrossWords(group, odd, minPeers) {
  const others = group.sites.length - 1;
  const at = plural(others, "other site");
  if (others < minPeers) {
    return { tone: "", text: `${group.name}: watched at ${at}. Comparing needs ${minPeers} other sites.` };
  }
  if (!odd.length) {
    return { tone: "ok", text: `${group.name}: connects to the same places here as at ${at}.` };
  }
  return {
    tone: "warn",
    text: `${group.name}: reaches ${odd.length === 1 ? "a place" : `${odd.length} places`} the ${group.name} at your ${at} do not.`,
  };
}

/* ── The AI analyst: what the behaviour model noted (pkg/firewall behaviour.go) ── */

export const ANALYST = {
  title: "AI analyst",
  sub: "Learns what each device on your network normally does, and flags what it never did, with the evidence.",
  learning: (devices, learnt) => (devices
    ? `Watching ${plural(devices, "device")}; ${learnt === devices ? "all" : count(learnt)} watched long enough to judge.`
    : "Watching your network: devices appear here as they connect."),
  quiet: "Nothing unusual. Notes appear here when a device does something its own history does not hold, such as a port it never used or an address on a known-bad list.",
  brief: "Brief me",
  briefQuestion: "Look at this box's firewall, its latest blocked tries and the AI analyst's findings. In plain words: is anything worrying, what is it most likely, and what should I do? Propose changes where they help.",
  describe: "Describe a rule in your own words",
  describeHint: "e.g. tills may only reach the betting system and the card processor, and staff laptops may not use Netflix during opening hours",
  describeGo: "Write it",
  describeQuestion: (text) => `Write firewall settings for this, using this box's device groups and rules, and propose them for me to confirm: ${text}`,
  explain: "Ask Ada",
  block: "Block this place",
  dismiss: "Dismiss",
  unavailable: "The AI analyst needs a newer build on this box.",
};

const portNames = { 22: "remote shell (SSH)", 23: "Telnet", 25: "sending mail directly", 135: "Windows remote calls", 139: "Windows file sharing", 445: "Windows file sharing", 1080: "a proxy", 1433: "a database", 3306: "a database", 3389: "remote desktop", 4444: "a port malware often uses", 5555: "Android remote debugging", 5900: "remote screen sharing (VNC)", 6667: "IRC, used to steer botnets", 6697: "IRC, used to steer botnets", 9001: "Tor", 9030: "Tor", 31337: "a port malware often uses" };

const bytes = (n) => fmtBytesDecimal(Number(n || 0));

/* One note of the analyst as a sentence: what the device did, and the
   evidence. */
export function noteWords(f, deviceName) {
  const dev = deviceName || f.address || f.device;
  const where = f.owner ? `${orgName(f.owner)} (${f.place})` : f.place;
  const port = f.port ? ` on port ${f.port}${portNames[f.port] ? `, ${portNames[f.port]}` : ""}` : "";
  switch (f.kind) {
    case "threat":
      return {
        title: `${dev} ${f.stopped ? "tried to reach" : "reached"} an address on a known-bad list`,
        detail: `${where}${port} is on ${listWords(f.lists || ["a known-bad list"])}.${f.stopped ? " The firewall stopped it." : " Turn on known-bad blocking to stop it."} A device that does this is often infected.`,
      };
    case "new_port":
      return {
        title: `${dev} used port ${f.port} for the first time`,
        detail: `It connected to ${where}${port}, which it never did before.`,
      };
    case "burst":
      return {
        title: `${dev} opened far more connections than usual`,
        detail: `${count(Math.round(f.count))} in an hour, where it usually opens about ${count(Math.round(f.usual))}.`,
      };
    case "scan":
      return {
        title: `${dev} contacted many new places at once`,
        detail: `${count(Math.round(f.count))} places it never used, in one hour. That looks like a scan.`,
      };
    case "odd_hour":
      return {
        title: `${dev} was active at an unusual hour`,
        detail: `It opened connections at ${String(f.count).padStart(2, "0")}:00, an hour it never does.`,
      };
    case "upload":
      return {
        title: `${dev} sent far more than usual`,
        detail: `${bytes(f.count)} in an hour, where it usually sends about ${bytes(f.usual)}.`,
      };
    default:
      return { title: `${dev}: ${f.kind}`, detail: "" };
  }
}

/* The analyst's Block this place, as a rule keeping every device from it. */
export function blockRuleFor(f) {
  const name = `Block ${f.owner ? orgName(f.owner) : shown(f.place)}`;
  return { name: Array.from(name).slice(0, 48).join(""), action: "block", direction: "out", remote: [f.place] };
}

export const blockPlaceAsk = (rule, v) => ({
  title: `${rule.name}?`,
  message: `Every device on this network stops reaching ${shown(rule.remote[0])} from now on, as a rule you can remove on Rules.${v.on ? "" : " The firewall is off, so this turns it on too."}`,
  confirmLabel: v.on ? "Block it" : "Turn on and block",
  variant: "danger",
});

export const noteQuestion = (f, deviceName) => {
  const w = noteWords(f, deviceName);
  return `The AI analyst noted: "${w.title}. ${w.detail}" Explain what this could be and what I should check, and propose a change if one helps.`;
};

/* ── Rules: an operator's own, the lists, countries and flood limits ── */

export const RULES = {
  title: "Your rules",
  needsOn: "Rules apply while the firewall is on.",
  older: "This box runs software from before rules, so it cannot hold them yet.",
  everythingAsk: {
    title: "Block everything?",
    message: "This rule keeps every device here from reaching anything outside, at all times: the site is offline until the rule goes. Name devices, places, ports or times to narrow it.",
    confirmLabel: "Block everything",
    variant: "danger",
  },
};

/* The rules tables, laid out as AWS lays out a security group's rules: one
   table a direction, a view and an edit mode, a type that fills in the
   protocol and port, sources as chips. */
export const RULE_TABLE = {
  tabs: { out: "Outbound rules", in: "Inbound rules" },
  means: {
    out: "What devices on your network may start. Judged top to bottom; the first rule that matches decides.",
    in: "What may reach this box from the internet. Judged top to bottom; the first rule that matches decides. This network and the Weavian cloud always reach it.",
  },
  edit: { out: "Edit outbound rules", in: "Edit inbound rules" },
  cols: {
    n: "#", id: "Rule ID", action: "Action", type: "Type", protocol: "Protocol", ports: "Port range",
    devices: "Devices", dest: "Destination", source: "Source", when: "When", desc: "Description", stopped: "Stopped",
  },
  filter: "Filter rules",
  noMatch: "No rule matches the filter.",
  none: { out: "No outbound rules yet.", in: "No inbound rules yet." },
  everything: { out: "Everything else your devices start", in: "Everything else from the internet" },
  defaultAction: { out: "allow", in: "block" },
  order: {
    out: "Device groups' limits apply before these rules; known-bad addresses and blocked countries after them.",
    in: "Known-bad addresses and blocked countries are stopped after your rules, before the management networks. Replies to what the box itself started always get back in.",
  },
  /* The rows every box has coming in, in the order they are judged. */
  fixed: {
    builtIn: "built-in",
    lan: { source: "This network", desc: "Devices on this box's own network, so nobody can be locked out from inside." },
    cloud: { source: "Weavian cloud", desc: "This console and Weavian support, through the box's own tunnel." },
    management: {
      type: "SSH and settings", none: "None yet",
      desc: "Your management networks, such as a head office's internet address.",
      search: "Address or network", label: "Management network",
    },
    locked: "Always there; it cannot be changed.",
  },
  managementSafe:
    "Check an address before you add it: if a change cuts this console off, the box undoes it within two minutes, but it cannot tell whether your office still reaches it.",
  add: "Add rule",
  cancel: "Cancel",
  preview: "Preview changes",
  hidePreview: "Hide changes",
  save: "Save rules",
  anywhere: "Anywhere",
  custom: "Custom",
  everyDevice: "Every device",
  someAddresses: "Some addresses",
  always: "Always",
  scheduled: "Scheduled",
  newRule: "New",
  up: (n) => `Move rule ${n} up`,
  down: (n) => `Move rule ${n} down`,
  remove: "Delete",
  placeSearch: "Address or network",
  deviceSearch: "Device address",
  descPlaceholder: "e.g. No mail from tills",
  days: [["Monday", "Mon"], ["Tuesday", "Tue"], ["Wednesday", "Wed"], ["Thursday", "Thu"], ["Friday", "Fri"], ["Saturday", "Sat"], ["Sunday", "Sun"]],
  from: "From",
  to: "to",
  boxTime: (offset) => `Every day when none is picked. Times are the box's own, UTC${offset || ""}.`,
  warnAnywhereIn:
    "Rules with source Anywhere let every address on the internet reach this box on the ports they open. We recommend allowing known addresses only.",
  warnEverything: "A rule blocking all traffic from every device to anywhere, at all times, takes the whole site offline.",
  problems: {
    ports: "Name a port or a range, such as 443 or 8000-8100.",
    port: (p) => `${p} is not a port or a range of ports.`,
    remote: "Add an address or network, or choose Anywhere.",
    local: "Add a device's address, or choose Every device.",
    inPorts: "A rule letting something in names the ports it opens: pick a type with a port.",
    sameTimes: "The start and end times are the same.",
    name: "A description of at most 48 characters.",
  },
  fixFirst: "Fix the rules marked below, then save.",
  changes: {
    title: "Changes",
    added: "Added",
    changed: "Changed",
    deleted: "Deleted",
    moved: "The order changed.",
    none: "No changes yet.",
    management: (added, removed) => `Management networks: ${[
      added.length ? `added ${listWords(added.map(shown))}` : "",
      removed.length ? `removed ${listWords(removed.map(shown))}` : "",
    ].filter(Boolean).join("; ")}.`,
  },
};

/* The types a rule can be, AWS's names for them: each fixes a protocol and a
   port, or leaves the port to be typed (custom). */
export const RULE_TYPES = [
  { key: "all", label: "All traffic", protocol: "", ports: [] },
  { key: "all-tcp", label: "All TCP", protocol: "tcp", ports: [] },
  { key: "all-udp", label: "All UDP", protocol: "udp", ports: [] },
  { key: "custom-tcp", label: "Custom TCP", protocol: "tcp", custom: true },
  { key: "custom-udp", label: "Custom UDP", protocol: "udp", custom: true },
  { key: "custom-both", label: "Custom TCP and UDP", protocol: "", custom: true },
  { key: "http", label: "HTTP", protocol: "tcp", ports: [80] },
  { key: "https", label: "HTTPS", protocol: "tcp", ports: [443] },
  { key: "ssh", label: "SSH", protocol: "tcp", ports: [22] },
  { key: "rdp", label: "RDP", protocol: "tcp", ports: [3389] },
  { key: "smtp", label: "SMTP", protocol: "tcp", ports: [25] },
  { key: "smtps", label: "SMTPS", protocol: "tcp", ports: [465] },
  { key: "submission", label: "SMTP submission", protocol: "tcp", ports: [587] },
  { key: "dns-udp", label: "DNS (UDP)", protocol: "udp", ports: [53] },
  { key: "dns-tcp", label: "DNS (TCP)", protocol: "tcp", ports: [53] },
  { key: "ntp", label: "NTP", protocol: "udp", ports: [123] },
  { key: "smb", label: "SMB", protocol: "tcp", ports: [445] },
  { key: "ftp", label: "FTP", protocol: "tcp", ports: [21] },
  { key: "telnet", label: "Telnet", protocol: "tcp", ports: [23] },
  { key: "imap", label: "IMAP", protocol: "tcp", ports: [143] },
  { key: "imaps", label: "IMAPS", protocol: "tcp", ports: [993] },
  { key: "pop3", label: "POP3", protocol: "tcp", ports: [110] },
  { key: "pop3s", label: "POP3S", protocol: "tcp", ports: [995] },
  { key: "mysql", label: "MySQL", protocol: "tcp", ports: [3306] },
  { key: "mssql", label: "MSSQL", protocol: "tcp", ports: [1433] },
  { key: "postgres", label: "PostgreSQL", protocol: "tcp", ports: [5432] },
  { key: "vnc", label: "VNC", protocol: "tcp", ports: [5900] },
  { key: "sip", label: "SIP", protocol: "udp", ports: [5060] },
];

export const protocolLabel = (protocol, ports) =>
  (protocol ? protocol.toUpperCase() : (ports || []).length ? "TCP and UDP" : "All");

export const portsLabel = (ports) => ((ports || []).length ? ports.map(String).join(", ") : "All");

/* What the type, protocol and port of a rule read as: the named type whose
   protocol and single port it has, else All or Custom. */
export function ruleType(r) {
  const ports = (r.ports || []).map(String);
  const named = RULE_TYPES.find((t) => !t.custom && t.protocol === (r.protocol || "") &&
    t.ports.length === ports.length && t.ports.every((p, i) => String(p) === ports[i]));
  if (named) return named;
  return RULE_TYPES.find((t) => t.custom && t.protocol === (r.protocol || ""));
}

export const whenLabel = (schedule) =>
  (schedule ? [daysWords(schedule.days) || "every day", `${schedule.from} to ${schedule.to}`].join(", ") : "Always");

/* An address or network as a person types one: IPv4 with an optional prefix,
   or IPv6. The box checks again. */
export function isPlace(text) {
  const [addr, bits, more] = String(text).trim().split("/");
  if (more !== undefined || addr === "") return false;
  if (bits !== undefined && !/^\d{1,3}$/.test(bits)) return false;
  const v4 = addr.split(".");
  if (v4.length === 4 && v4.every((o) => /^\d{1,3}$/.test(o) && Number(o) <= 255)) return bits === undefined || Number(bits) <= 32;
  return /^[0-9a-f:.]+$/i.test(addr) && addr.includes(":") && (bits === undefined || Number(bits) <= 128);
}

export const notAPlace = (text) => `${text} is not an address or a network, such as 203.0.113.9 or 203.0.113.0/24.`;

const daysWords = (days) => {
  const d = days || [];
  const weekdays = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"];
  if (!d.length) return "";
  if (d.length === 5 && weekdays.every((x) => d.includes(x))) return "weekdays";
  if (d.length === 2 && d.includes("Saturday") && d.includes("Sunday")) return "weekends";
  return listWords(d.map((x) => x.slice(0, 3)));
};

/* A rule as the sentence it stands for. */
export function ruleSentence(r, groupName) {
  const where = (r.remote || []).length ? listWords(r.remote.map(shown)) : "anywhere";
  const ports = (r.ports || []).length
    ? ` on ${r.ports.length === 1 ? "port" : "ports"} ${listWords(r.ports.map(String))}${r.protocol ? ` (${r.protocol.toUpperCase()})` : ""}`
    : r.protocol ? ` over ${r.protocol.toUpperCase()}` : "";
  const when = r.schedule ? `, ${[daysWords(r.schedule.days), `${r.schedule.from} to ${r.schedule.to}`].filter(Boolean).join(" ")}` : "";
  if (r.direction === "in") {
    return r.action === "allow" ? `Lets ${where} reach the box${ports}${when}.` : `Keeps ${where} from reaching the box${ports}${when}.`;
  }
  const who = r.group ? `devices in ${groupName || r.group}` : (r.local || []).length ? listWords(r.local.map(shown)) : "every device";
  return r.action === "allow"
    ? `Lets ${who} reach ${where}${ports}${when}, past the lists and countries.`
    : `Keeps ${who} from reaching ${where}${ports}${when}.`;
}

export const LISTS = {
  title: "Known-bad addresses",
  means: "Public lists of addresses run by criminals, botnets and compromised machines, refreshed every six hours. With them on, nothing on your network reaches them and they cannot reach the box.",
  on: "Block known-bad addresses",
  entries: (n) => plural(n, "address range"),
  updated: (iso) => (iso && !String(iso).startsWith("0001") ? `updated ${dayWords(iso)}` : "not fetched yet"),
};

export const COUNTRIES = {
  title: "Countries",
  means: "Stop your network reaching whole countries, and the box being reached from them. A service hosted in a blocked country stops working for your network too.",
  pending: "The box is loading which addresses belong to each country; blocking starts within a minute or two.",
  add: "Add a country…",
  none: "No country is blocked.",
};

export const FLOOD = {
  title: "Flood limits",
  text: "Always on with the firewall: one address may open 60 connections a minute to the box's settings and command line, and the box answers at most 20 pings a second.",
};

export const countryName = (code) => {
  try {
    return new Intl.DisplayNames(["en"], { type: "region" }).of(code) || code;
  } catch {
    return code;
  }
};

export const COUNTRY_CODES = "AD AE AF AG AI AL AM AO AR AS AT AU AW AX AZ BA BB BD BE BF BG BH BI BJ BL BM BN BO BQ BR BS BT BW BY BZ CA CD CF CG CH CI CK CL CM CN CO CR CU CV CW CY CZ DE DJ DK DM DO DZ EC EE EG ER ES ET FI FJ FK FM FO FR GA GB GD GE GF GG GH GI GL GM GN GP GQ GR GT GU GW GY HK HN HR HT HU ID IE IL IM IN IQ IR IS IT JE JM JO JP KE KG KH KI KM KN KP KR KW KY KZ LA LB LC LI LK LR LS LT LU LV LY MA MC MD ME MF MG MH MK ML MM MN MO MP MQ MR MS MT MU MV MW MX MY MZ NA NC NE NF NG NI NL NO NP NR NU NZ OM PA PE PF PG PH PK PL PM PR PS PT PW PY QA RE RO RS RU RW SA SB SC SD SE SG SI SK SL SM SN SO SR SS ST SV SX SY SZ TC TD TG TH TJ TL TM TN TO TR TT TV TW TZ UA UG US UY UZ VA VC VE VG VI VN VU WF WS XK YE YT ZA ZM ZW".split(" ");

/* ── The record of blocked tries (pkg/firewall blocklog.go) ── */

export const LOG = {
  title: "Latest blocked tries",
  means: "Each try the firewall stopped: when, what tried, where to, and which rule stopped it. Kept 14 days on the box.",
  none: "Nothing recorded yet.",
  explain: "Explain",
  cols: { when: "When", from: "From", to: "To", why: "Stopped by" },
  missed: (lost, unwritten) => [
    Number(lost) ? "The box fell behind at times, and some tries then went unrecorded." : "",
    Number(unwritten) ? `${plural(unwritten, "try", "tries")} past a day's limit on the box were counted but not kept.` : "",
  ].filter(Boolean).join(" "),
  rate: "At most 20 tries a second are recorded for each kind; the counts above include every one.",
  server: "Send to your log system",
  serverMeans: "Each blocked try also goes to your own log server as syslog over UDP, for audits such as PCI.",
  serverPlaceholder: "host:port, e.g. 192.0.2.10:514",
  serverSave: "Save",
  serverClear: "Stop sending",
  serverNow: (server) => `Sending every blocked try to ${server}.`,
  unavailable: "The record of blocked tries needs a newer build on this box.",
  filters: [
    ["", "Everything"],
    ["internet", "Uninvited, from outside"],
    ["jail", "Banned here"],
    ["fleet", "Banned at other sites"],
    ["feeds", "Crowdsourced lists"],
    ["threat", "Known-bad addresses"],
    ["country", "Blocked countries"],
    ["rule_", "Your rules"],
    ["held_", "Device groups"],
    ["lan_to_boxes", "Toward other sites' boxes"],
  ],
};

/* How much a try asks of anyone: amber for something a device here did that
   it should not, grey for the internet's noise. ours is whether a device of
   this network made it. */
export const whyTone = (why, ours) =>
  (String(why).startsWith("held_") || why === "lan_to_boxes" || (why === "threat" && ours) ? "look" : "calm");

/* Why a try was stopped, in words: the counter it counted into. */
export function whyWords(why, rules, groups) {
  if (why === "internet") return "uninvited, from outside";
  if (why === "tunnel") return "over the Weavian cloud, not from the cloud";
  if (why === "lan_to_boxes") return "toward another site's box";
  if (why === "threat") return "known-bad address";
  if (why === "country") return "blocked country";
  if (why === "flood") return "flood limit";
  if (why === "jail") return "banned here";
  if (why === "fleet") return "banned at other sites";
  if (why === "feeds") return "crowdsourced attacker list";
  if (String(why).startsWith("rule_")) {
    const r = (rules || []).find((x) => `rule_${x.id}` === why);
    return r ? `your rule "${r.name}"` : "one of your rules";
  }
  if (String(why).startsWith("held_")) {
    const g = (groups || []).find((x) => String(why).startsWith(`held_${x.id}`));
    return g ? `${g.name}: not on its list` : "a device group's list";
  }
  return why;
}

export const logQuestion = (e, words) =>
  `Explain this blocked try in plain words, and whether it matters: at ${e.at}, ${e.src}${e.src_port ? `:${e.src_port}` : ""} to ${e.dst}${e.dst_port ? `:${e.dst_port}` : ""} (${e.proto})${e.device ? ` from device ${e.device}` : ""}, stopped by ${words}.`;

/* ── Copy to other sites ── */

export const COPY = {
  button: "Copy to other sites",
  title: "Copy this box's firewall",
  means: "Puts this box's choices on other boxes of the same customer. Each box keeps its own devices and confirms its own change.",
  what: {
    settings: { label: "Firewall settings", text: "On or off, management networks, known-bad lists, countries, your rules and the log server." },
    groups: { label: "Device group definitions", text: "Each group's name, where it may go and what it blocks, matched by name. Not its devices: each site picks its own." },
  },
  sites: "To these sites",
  none: "No other site of this customer is listed.",
  go: (n) => (n ? `Copy to ${plural(n, "site")}` : "Pick the sites"),
  done: "Copied.",
  failed: "Not copied",
  copying: "Copying…",
  offline: "offline",
  pickAll: "Pick every site online",
  clearAll: "Clear",
  noAnswer: "the box did not answer",
  noFirewall: "this box cannot run the firewall",
  noGroups: "this box has no device groups",
  older: "this box's software does not take rules, lists or countries yet",
  leftOut: (rules) => `Left out ${plural(rules.length, "rule")} naming a group that site does not have: ${listWords(rules.map((r) => r.name))}.`,
  groupsRulesNote: "A rule naming a device group is copied to a site that has a group of the same name. Copying the group definitions too makes one, without devices: pick them at that site.",
  summary: (st) => {
    if (!st.on) return "Firewall off.";
    const parts = ["Firewall on"];
    const nets = (st.management_networks || []).length;
    if (nets) parts.push(plural(nets, "management network"));
    if (st.blocklists) parts.push("known-bad addresses blocked");
    if ((st.block_countries || []).length) parts.push(plural(st.block_countries.length, "country", "countries") + " blocked");
    if ((st.rules || []).length) parts.push(plural(st.rules.length, "rule"));
    if (st.log_server) parts.push(`blocked tries sent to ${st.log_server}`);
    return `${listWords(parts)}.`;
  },
  groupsSummary: (groups, named) => [
    groups.length ? `${listWords(groups.map((g) => g.name))}, with their places and blocked kinds.` : "No group here holds devices to places or blocks traffic.",
    named.length ? `${listWords(named.map((g) => g.name))}, which your rules name, made empty where a site has none.` : "",
  ].filter(Boolean).join(" "),
};
